/**
 * The Recordings list (design book "Recording lifecycle", W1): one row per recording the server holds, with the
 * lifecycle status from the server's `phase` (Not sent yet · Can't be used · Needs labels · Fix labels · In review ·
 * Scored · Not accepted), quick chips (All · Needs you · In review · Results), the Labels and Review filters, and a
 * one-line labels summary. This file is the console's one status vocabulary: every recording screen reads its words
 * here. Pure functions; the page only renders them.
 */
import { humanize, type MyCapture, type MyCaptureRow, type MyUnsubmitted, type RecordingLabel, type RecordingPhase } from '@robotensor/api'

/** A row of the list: a sent recording, or one the server holds that wasn't sent for review. */
export type ListedRecording = MyCapture | MyUnsubmitted

// ── Status ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The status a row shows. `other`: a phase this build doesn't know, shown with the server's words. */
export type RecordingKey = 'unsubmitted' | 'unusable' | 'needs_labels' | 'fix' | 'in_review' | 'scored' | 'not_accepted' | 'practice' | 'other'
export type RecordingTone = 'fix' | 'wait' | 'ok' | 'no' | 'practice' | 'neutral'

export const STATUS_WORDS: Record<Exclude<RecordingKey, 'other'>, { label: string; tone: RecordingTone }> = {
  unsubmitted: { label: 'Not sent yet', tone: 'wait' },
  unusable: { label: 'Can’t be used', tone: 'no' },
  needs_labels: { label: 'Needs labels', tone: 'fix' },
  fix: { label: 'Fix labels', tone: 'fix' },
  in_review: { label: 'In review', tone: 'wait' },
  scored: { label: 'Scored', tone: 'ok' },
  not_accepted: { label: 'Not accepted', tone: 'no' },
  practice: { label: 'Practice', tone: 'practice' },
}

/** Older servers send no `phase`: the same mapping from the submission status (lifecycle.md). */
const FROM_STATUS: Record<string, RecordingPhase> = {
  needs_labels: 'needs_labels',
  queued: 'in_review',
  validating: 'in_review',
  checking: 'in_review',
  needs_review: 'in_review',
  in_review: 'in_review',
  needs_correction: 'labels_rejected',
  accepted: 'scored',
  partially_accepted: 'scored',
  rejected: 'not_accepted',
  practice_done: 'practice',
  unsubmitted: 'unsubmitted',
}

const PHASES: readonly RecordingPhase[] = ['recording', 'unsubmitted', 'unusable', 'needs_labels', 'in_review', 'labels_rejected', 'scored', 'not_accepted', 'practice', 'withdrawn']

/** The recording's phase: the server's `phase`, else read from its status; null for one this build doesn't know. */
export function phaseOf(c: Pick<MyCapture, 'phase' | 'status' | 'practice'>): RecordingPhase | null {
  if (c.practice) return 'practice'
  const p = c.phase ?? FROM_STATUS[c.status]
  return p && (PHASES as readonly string[]).includes(p) ? (p as RecordingPhase) : null
}

const KEY_OF: Record<RecordingPhase, RecordingKey> = {
  recording: 'unsubmitted',
  unsubmitted: 'unsubmitted',
  unusable: 'unusable',
  needs_labels: 'needs_labels',
  labels_rejected: 'fix',
  in_review: 'in_review',
  scored: 'scored',
  not_accepted: 'not_accepted',
  practice: 'practice',
  withdrawn: 'other',
}

export function recordingStatus(c: Pick<MyCaptureRow, 'phase' | 'status' | 'practice' | 'status_label'>): { key: RecordingKey; label: string; tone: RecordingTone } {
  const p = phaseOf(c)
  const key = p ? KEY_OF[p] : 'other'
  if (key === 'other') return { key, label: c.status_label?.trim() || humanize(String(c.phase ?? c.status ?? 'unknown')), tone: 'neutral' }
  return { key, ...STATUS_WORDS[key] }
}

// ── Filters (kept in the address) ───────────────────────────────────────────────────────────────────────────

export const QUICK = ['needs_you', 'in_review', 'results'] as const
export type Quick = (typeof QUICK)[number]
export const QUICK_LABEL: Record<Quick, string> = { needs_you: 'Needs you', in_review: 'In review', results: 'Results' }

export const LABELS_FILTER = ['open', 'done'] as const
export type LabelsFilter = (typeof LABELS_FILTER)[number]
export const LABELS_LABEL: Record<LabelsFilter, string> = { open: 'Not finished', done: 'Finished' }

export const REVIEW_FILTER = ['waiting', 'reviewed'] as const
export type ReviewFilter = (typeof REVIEW_FILTER)[number]
export const REVIEW_LABEL: Record<ReviewFilter, string> = { waiting: 'Waiting', reviewed: 'Reviewed' }

export interface RecordingsSearch {
  status?: Quick
  labels?: LabelsFilter
  review?: ReviewFilter
}

/** The `?status=` quick chip; older links said fix, needs_correction, checking, review, accepted, rejected… */
export function recordingsFilter(v: unknown): Quick | undefined {
  if (typeof v !== 'string') return undefined
  if ((QUICK as readonly string[]).includes(v)) return v as Quick
  if (['fix', 'needs_correction', 'needs_labels', 'labels_rejected', 'phone', 'sending'].includes(v)) return 'needs_you'
  if (['checking', 'review'].includes(v)) return 'in_review'
  if (['accepted', 'rejected', 'scored', 'not_accepted', 'practice'].includes(v)) return 'results'
  return undefined
}

const pick = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined)

export function recordingsSearch(s: Record<string, unknown>): RecordingsSearch {
  return { status: recordingsFilter(s.status), labels: pick(s.labels, LABELS_FILTER), review: pick(s.review, REVIEW_FILTER) }
}

/** Not sent yet and can't be used need their owner too: send it from the phone, or delete it. */
const QUICK_OF: Record<RecordingKey, Quick | null> = { unsubmitted: 'needs_you', unusable: 'needs_you', needs_labels: 'needs_you', fix: 'needs_you', in_review: 'in_review', scored: 'results', not_accepted: 'results', practice: 'results', other: null }
/** Labels finished: locked by Finish (and open again while a reviewer's notes are being fixed). */
const LABELS_OF: Record<RecordingKey, LabelsFilter | null> = { unsubmitted: 'open', unusable: null, needs_labels: 'open', fix: 'open', in_review: 'done', scored: 'done', not_accepted: 'done', practice: 'done', other: null }
/** Reviewed: a reviewer has decided at least once. */
const REVIEW_OF: Record<RecordingKey, ReviewFilter | null> = { unsubmitted: null, unusable: null, needs_labels: 'waiting', in_review: 'waiting', fix: 'reviewed', scored: 'reviewed', not_accepted: 'reviewed', practice: 'reviewed', other: null }

// ── Rows ────────────────────────────────────────────────────────────────────────────────────────────────────

export interface RecordingRow {
  id: string
  key: RecordingKey
  status: { label: string; tone: RecordingTone }
  title: string
  /** A collection request recording. */
  request: boolean
  at: string | null
  durationMs: number | null
  labels: RecordingLabel[]
  /** One detail under the labels: "2 of 3 parts labelled", "2 notes from the reviewer", or the server's own words for
   * why a recording can't be used or sent. */
  detail: string | null
  /** "+42", or null for "—". */
  points: string | null
  /** What the row's button does (`delete` only where the server says the owner may). */
  action: 'label' | 'fix' | 'report' | 'delete' | null
  quick: Quick | null
  labelsState: LabelsFilter | null
  reviewState: ReviewFilter | null
}

/** "Wed 4 Nov, 14:02". */
export function dateTime(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '')
  return `${day}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

/** What a recording is called: the server's `name` (main activity · place, or the request), else "Recording · Wed 4
 * Nov, 14:02". A server without `name` (older): the first part's title, else a request's title, else the date. */
export function recordingName(c: Pick<MyCapture, 'name' | 'title' | 'kind' | 'mission_title' | 'recorded_at'> & { created_at?: string | null }): string {
  const n = c.name?.trim()
  if (n) return n
  if (c.name === undefined) {
    const t = c.title?.trim() || (c.kind === 'campaign' ? c.mission_title?.trim() : '')
    if (t) return t
  }
  const at = dateTime(c.recorded_at ?? c.created_at)
  return at ? `Recording · ${at}` : 'Recording'
}

function detailOf(c: ListedRecording, key: RecordingKey): string | null {
  // the server's words, as is: why it can't be used, or what keeps it from being sent (a recording too short)
  if (key === 'unusable') return c.problem?.message ?? null
  if (key === 'unsubmitted') return c.problems?.find((p) => p.target === 'recording')?.message ?? null
  if (key === 'needs_labels') {
    const total = c.parts_total ?? 0
    const done = c.parts_labelled ?? 0
    return total > 0 && (c.labels?.length ?? 0) > 0 && done < total ? `${done} of ${total} part${total === 1 ? '' : 's'} labelled` : null
  }
  if (key === 'fix') {
    const n = c.notes ?? c.corrections?.length ?? 0
    return n > 0 ? `${n} note${n === 1 ? '' : 's'} from the reviewer` : 'See the reviewer’s notes'
  }
  return null
}

export function recordingRow(c: ListedRecording): RecordingRow {
  const st = recordingStatus(c as MyCaptureRow)
  const key = st.key
  return {
    id: c.id,
    key,
    status: { label: st.label, tone: st.tone },
    title: recordingName(c),
    request: c.kind === 'campaign',
    at: c.recorded_at ?? c.created_at ?? null,
    durationMs: c.kept_ms ?? c.duration_ms ?? null,
    labels: Array.isArray(c.labels) ? c.labels : [],
    detail: detailOf(c, key),
    points: key === 'scored' ? `+${c.points}` : null,
    action: key === 'needs_labels' ? 'label' : key === 'fix' ? 'fix' : key === 'scored' || key === 'not_accepted' ? 'report' : (key === 'unsubmitted' || key === 'unusable') && c.can_delete ? 'delete' : null,
    quick: QUICK_OF[key],
    labelsState: LABELS_OF[key],
    reviewState: REVIEW_OF[key],
  }
}

/** Every recording the server holds (sent ones, and `unsubmitted` ones: not sent yet or refused when sent), once
 * each, newest first. Withdrawn ones aren't listed. */
export function recordingRows(items: readonly ListedRecording[]): RecordingRow[] {
  const seen = new Set<string>()
  return items
    .filter((c) => c.status !== 'withdrawn' && c.phase !== 'withdrawn' && !seen.has(c.id) && !!seen.add(c.id))
    .map(recordingRow)
    .sort((a, b) => (Date.parse(b.at ?? '') || 0) - (Date.parse(a.at ?? '') || 0))
}

export function matches(r: RecordingRow, f: RecordingsSearch): boolean {
  return (!f.status || r.quick === f.status) && (!f.labels || r.labelsState === f.labels) && (!f.review || r.reviewState === f.review)
}

export function filterRows(rows: readonly RecordingRow[], f: RecordingsSearch): RecordingRow[] {
  return rows.filter((r) => matches(r, f))
}

export interface FacetCounts {
  status: Record<Quick | 'all', number>
  labels: Record<LabelsFilter | 'any', number>
  review: Record<ReviewFilter | 'any', number>
}

/** Each option's count with the other groups' choices applied (not its own), so a count says what choosing it shows;
 * an option at 0 is greyed out. */
export function facetCounts(rows: readonly RecordingRow[], f: RecordingsSearch): FacetCounts {
  const out: FacetCounts = { status: { all: 0, needs_you: 0, in_review: 0, results: 0 }, labels: { any: 0, open: 0, done: 0 }, review: { any: 0, waiting: 0, reviewed: 0 } }
  for (const r of rows) {
    if (matches(r, { ...f, status: undefined })) {
      out.status.all += 1
      if (r.quick) out.status[r.quick] += 1
    }
    if (matches(r, { ...f, labels: undefined })) {
      out.labels.any += 1
      if (r.labelsState) out.labels[r.labelsState] += 1
    }
    if (matches(r, { ...f, review: undefined })) {
      out.review.any += 1
      if (r.reviewState) out.review[r.reviewState] += 1
    }
  }
  return out
}

// ── The labels line ─────────────────────────────────────────────────────────────────────────────────────────

/** Chip colour by role: places green, main activities blue, the rest neutral. */
export function labelTone(role: string): 'place' | 'activity' | 'plain' {
  return role === 'place' || role === 'environment' ? 'place' : role === 'activity' ? 'activity' : 'plain'
}

/** Distinct labels in the list order (places, main activities, then the rest as they first appear), whatever order
 * the server sent them in. */
export function orderedLabels(labels: readonly RecordingLabel[]): RecordingLabel[] {
  const seen = new Set<string>()
  const rank = (l: RecordingLabel) => (labelTone(l.role) === 'place' ? 0 : labelTone(l.role) === 'activity' ? 1 : 2)
  return labels
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => {
      const id = l.key ?? `name:${l.name}`
      if (!l.name || seen.has(id)) return false
      seen.add(id)
      return true
    })
    .sort((a, b) => rank(a.l) - rank(b.l) || a.i - b.i)
    .map(({ l }) => l)
}

/**
 * How many chips fit on one line of `available` px: all of them when they fit, else as many as leave room for the
 * "+N" chip (`moreWidth(N)`). Without a layout (`available` 0, e.g. before the first paint) everything shows.
 */
export function labelsThatFit(widths: readonly number[], available: number, gap: number, moreWidth: (rest: number) => number): number {
  const n = widths.length
  if (available <= 0) return n
  const used = (k: number) => widths.slice(0, k).reduce((a, w) => a + w, 0) + gap * Math.max(0, k - 1)
  if (used(n) <= available) return n
  for (let k = n - 1; k > 0; k--) if (used(k) + gap + moreWidth(n - k) <= available) return k
  return 0
}

/** "Bedroom, Fold clothes, Towel": what a screen reader hears instead of "+N". */
export function labelsText(labels: readonly RecordingLabel[]): string {
  return labels.length ? `Labels: ${labels.map((l) => l.name).join(', ')}` : 'No labels yet'
}

// ── Formatting ──────────────────────────────────────────────────────────────────────────────────────────────

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/** "Today", "Yesterday", "Sun", "12 Oct". */
export function when(iso: string | null, now = new Date()): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days > 1 && days < 7) return d.toLocaleDateString('en-GB', { weekday: 'short' })
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** "12:40", "1:02:05". */
export function length(ms: number | null): string {
  if (ms == null) return '–'
  const s = Math.round(ms / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

// ── What's missing before Finish ────────────────────────────────────────────────────────────────────────────

const NEED_WORDS: Record<string, string> = {
  environment: 'a place',
  environment_evidence: 'proof for its place',
  activity: 'an activity',
  activity_evidence: 'proof for its activity',
}

/** "Part 3 needs a place and an activity"; "Add a part first" without any part. */
export function missingText(partNo: number | null, needs: readonly string[]): string {
  if (needs.includes('episode')) return 'Add a part with a place and a main activity'
  const words = joinAnd(needs.map((n) => NEED_WORDS[n] ?? humanize(n).toLowerCase()))
  return partNo === null ? `A part needs ${words}` : `Part ${partNo} needs ${words}`
}

function joinAnd(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? 'labels'
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/** What a reviewer asked for, in a few words. */
export const CORRECTION_SHORT: Record<string, string> = {
  labels_unsupported: 'some labels aren’t shown in the video',
  episode_boundaries: 'a part starts or ends in the wrong place',
  missing_evidence: 'some labels need proof that shows them',
  needs_labels: 'every part needs a place and a main activity with proof',
}
